import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import SuccessModal from "../../../../../../components/common/Modal/SuccessModal";
import SuccessIcon from "../../../../../../../svg/SuccessIcon";

const stripePromise = loadStripe("your-stripe-public-key"); // Replace with your Stripe public key

const cardElementOptions = {
    style: {
        base: {
            fontSize: "16px",
            color: "#424770",
            fontFamily: "system-ui, -apple-system, sans-serif",
            "::placeholder": {
                color: "#aab7c4",
            },
            padding: "10px 0",
        },
        invalid: {
            color: "#e53e3e",
        },
    },
    hidePostalCode: true,
};

const CheckoutForm = ({ amount }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

    const savedPaymentMethod = {
        id: "pm_123456789",
        card: {
            brand: "visa",
            last4: "4242",
            exp_month: 12,
            exp_year: 2025,
        },
        isDefault: true,
    };

    const [showNewCardForm, setShowNewCardForm] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe) return;

        setProcessing(true);
        setError(null);

        // Your payment processing code would go here
        
        // Simulate successful payment
        handlePayment();
        setProcessing(false);
    };

    const handlePaymentMethodChange = (e) => {
        const value = e.target.value;
        if (value === "new") {
            setShowNewCardForm(true);
        } else {
            setShowNewCardForm(false);
        }
    };

    const formatCardBrand = (brand) => brand.charAt(0).toUpperCase() + brand.slice(1);

    // Replace toast with modal
    const handlePayment = () => {
        setIsSuccessModalOpen(true);
    };

    return (
        <>
            <form onSubmit={handleSubmit}>
                <h2 className="text-lg font-medium text-indigo-900 mb-1">Pay with Stripe</h2>
                <p className="text-sm text-gray-500 mb-6">Secure and encrypted payment</p>

                <div className="mb-4">
                    <label className="block text-sm text-gray-600 mb-2">Payment Method</label>
                    <div className="space-y-2">
                        <label className="d-flex gap-2 items-center p-3 rounded-lg bg-white cursor-pointer">
                            <input
                                type="radio"
                                name="paymentMethod"
                                value={savedPaymentMethod.id}
                                checked={!showNewCardForm}
                                onChange={handlePaymentMethodChange}
                                className="form-check-input"
                            />
                            <div className="flex-1">
                                <div className="d-flex align-items-center">
                                    <span className="fw-medium">
                                        {formatCardBrand(savedPaymentMethod.card.brand)} •••• {savedPaymentMethod.card.last4}
                                    </span>
                                    {savedPaymentMethod.isDefault && (
                                        <span className="ms-2 px-2 py-1 text-xs bg-primary text-white rounded-pill">
                                            Default
                                        </span>
                                    )}
                                </div>
                                <div className="text-muted small">
                                    Expires {savedPaymentMethod.card.exp_month}/{savedPaymentMethod.card.exp_year.toString().substr(-2)}
                                </div>
                            </div>
                        </label>

                        <label className="d-flex items-center p-3 gap-2 rounded-lg bg-white cursor-pointer">
                            <input
                                type="radio"
                                name="paymentMethod"
                                value="new"
                                checked={showNewCardForm}
                                onChange={handlePaymentMethodChange}
                                className="form-check-input"
                            />
                            <span className="fw-medium">Add new payment method</span>
                        </label>
                    </div>
                </div>

                {showNewCardForm && (
                    <div className="mt-4">
                        <label className="block text-sm text-gray-600 mb-2">Card Details</label>
                        <div className="border border-gray-200 rounded-lg p-3 focus-within:border-indigo-500 transition bg-gray-50">
                            <CardElement options={cardElementOptions} />
                        </div>
                    </div>
                )}

                {error && <p className="mt-3 text-xs text-red-500">{error}</p>}

                <button
                    type="submit"
                    disabled={!stripe || processing || (showNewCardForm && !elements)}
                    className="mt-5 btn btn-primary w-full py-3 text-sm font-medium rounded-lg transition duration-200 flex items-center justify-center bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-indigo-300"
                    style={{ minWidth: "131px" }}
                >
                    {processing ? "Processing..." : `Pay $${amount || '0.00'}`}
                </button>
            </form>

            <SuccessModal
                isOpen={isSuccessModalOpen}
                onClose={() => setIsSuccessModalOpen(false)}
                title="Payment Successful"
                message={`Your payment of $${amount || '0.00'} has been processed successfully.`}
                icon={<SuccessIcon width="60" height="60"/>}
            />
        </>
    );
};

const StripeCheckout = ({ amount }) => (
    <Elements stripe={stripePromise}>
        <CheckoutForm amount={amount} />
    </Elements>
);

export default StripeCheckout;